import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  runTransaction,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { sanitizeInput } from '../config/security';

/**
 * Validate start/end and that start < end and are within reasonable bounds
 */
const validateWindow = (startDate, endDate) => {
  if (!startDate || !endDate) throw new Error('Start and end are required');
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) throw new Error('Invalid date');
  if (endMs <= startMs) throw new Error('End must be after start');
  const now = Date.now();
  if (endMs <= now) throw new Error('Reservation must be in the future');
  // Limit to 2 days max duration based on lab lead feedback
  const maxDurationMs = 2 * 24 * 60 * 60 * 1000; // 2 days
  if ((endMs - startMs) > maxDurationMs) throw new Error('Reservation cannot be longer than 2 days');
};

/**
 * Convert JS Date to Firestore Timestamp
 */
const toTs = (d) => Timestamp.fromDate(d);

/**
 * Check for overlaps for a single item
 */
export const hasReservationConflict = async (itemId, startDate, endDate) => {
  validateWindow(startDate, endDate);
  const reservationsRef = collection(db, 'items', itemId, 'reservations');
  // Use a single inequality on 'start' to avoid composite index and multi-field range
  const q1 = query(
    reservationsRef,
    where('start', '<', toTs(endDate)),
    orderBy('start', 'asc')
  );
  const snap = await getDocs(q1);
  let conflict = false;
  snap.forEach((d) => {
    const r = d.data();
    if (r.status === 'canceled' || r.status === 'returned') return;
    const rStartMs = (r.start instanceof Timestamp ? r.start.toMillis() : (r.start?.toDate?.() || new Date(r.start)).getTime());
    const rEndMs = (r.end instanceof Timestamp ? r.end.toMillis() : (r.end?.toDate?.() || new Date(r.end)).getTime());
    // Check for actual overlap: existing end > new start AND existing start < new end
    if (rEndMs > startDate.getTime() && rStartMs < endDate.getTime()) {
      conflict = true;
    }
  });
  return conflict;
};

/**
 * Create a reservation (auto-approved) for an item.
 * Requires: userId or pid/name. If userId is absent (guest flow), pid is required.
 */
export const createReservation = async (itemId, {
  userId = null,
  userName = null,
  pid = null,
  start,
  end,
  notes = ''
}) => {
  const startDate = (start instanceof Date) ? start : new Date(start);
  const endDate = (end instanceof Date) ? end : new Date(end);
  validateWindow(startDate, endDate);

  if (!userId && !pid) {
    throw new Error('PID is required for guest reservations');
  }

  const itemRef = doc(db, 'items', itemId);
  const reservationsRef = collection(db, 'items', itemId, 'reservations');

  return await runTransaction(db, async (tx) => {
    // Ensure item exists
    const itemSnap = await tx.get(itemRef);
    if (!itemSnap.exists()) throw new Error('Item not found');

    // Conflict check inside transaction - use single range filter to avoid composite index
    const qOverlap = query(
      reservationsRef,
      where('start', '<', toTs(endDate)),
      orderBy('start', 'asc')
    );
    const overlapSnap = await getDocs(qOverlap);
    let hasConflict = false;
    overlapSnap.forEach((doc) => {
      const data = doc.data();
      if (data.status === 'canceled' || data.status === 'returned') return;
      const reservationStartMs = (data.start instanceof Timestamp ? data.start.toMillis() : (data.start?.toDate?.() || new Date(data.start)).getTime());
      const reservationEndMs = (data.end instanceof Timestamp ? data.end.toMillis() : (data.end?.toDate?.() || new Date(data.end)).getTime());
      // Check for actual overlap: existing end > new start AND existing start < new end
      if (reservationEndMs > startDate.getTime() && reservationStartMs < endDate.getTime()) {
        hasConflict = true;
      }
    });
    if (hasConflict) {
      throw new Error('This time conflicts with an existing reservation');
    }

    const payload = {
      itemId,
      userId: userId || null,
      userName: userName ? sanitizeInput.text(userName) : null,
      pid: pid || null,
      start: toTs(startDate),
      end: toTs(endDate),
      status: 'reserved',
      notes: sanitizeInput.text(notes || ''),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const newRef = await addDoc(reservationsRef, payload);
    return { id: newRef.id, ...payload };
  });
};

/**
 * List reservations for an item in a given range.
 */
export const listItemReservations = async (itemId, { from = null, to = null } = {}) => {
  const reservationsRef = collection(db, 'items', itemId, 'reservations');
  let qBase = reservationsRef;

  // Range: events overlapping window [from, to]
  if (from && to) {
    const fromDate = (from instanceof Date) ? from : new Date(from);
    const toDate = (to instanceof Date) ? to : new Date(to);
    qBase = query(
      reservationsRef,
      where('start', '<', toTs(toDate)),
      orderBy('start', 'asc')
    );
    const snap = await getDocs(qBase);
    const filtered = [];
    snap.forEach((d) => {
      const data = { id: d.id, ...d.data() };
      const endMs = (data.end instanceof Timestamp ? data.end.toMillis() : (data.end?.toDate?.() || new Date(data.end)).getTime());
      if (endMs > fromDate.getTime()) {
        filtered.push(data);
      }
    });
    filtered.sort((a, b) => a.start.toMillis() - b.start.toMillis());
    return filtered;
  } else {
    qBase = query(reservationsRef, orderBy('start', 'asc'));
    const snap = await getDocs(qBase);
    const results = [];
    snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
    results.sort((a, b) => a.start.toMillis() - b.start.toMillis());
    return results;
  }
};

/**
 * Cancel own reservation by id.
 */
export const cancelReservation = async (itemId, reservationId) => {
  const resRef = doc(db, 'items', itemId, 'reservations', reservationId);
  // Minimal client implementation: set status canceled.
  // Using transaction to avoid races with future features.
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(resRef);
    if (!snap.exists()) throw new Error('Reservation not found');
    const data = snap.data();
    if (data.status !== 'reserved') throw new Error('Only reserved reservations can be canceled');
    tx.update(resRef, { status: 'canceled', updatedAt: serverTimestamp() });
    return { id: reservationId, ...data, status: 'canceled' };
  });
};

/**
 * Return/end a reservation early (for both reserved and checked_out status)
 */
export const returnReservationEarly = async (itemId, reservationId) => {
  const itemRef = doc(db, 'items', itemId);
  const resRef = doc(db, 'items', itemId, 'reservations', reservationId);
  
  return runTransaction(db, async (tx) => {
    const itemSnap = await tx.get(itemRef);
    const resSnap = await tx.get(resRef);
    
    if (!itemSnap.exists()) throw new Error('Item not found');
    if (!resSnap.exists()) throw new Error('Reservation not found');
    
    const itemData = itemSnap.data();
    const resData = resSnap.data();
    
    // Can only return active reservations (reserved or checked_out)
    if (resData.status !== 'reserved' && resData.status !== 'checked_out') {
      throw new Error('Can only return active reservations');
    }
    
    // If the item is currently checked out for this reservation, make it available
    if (itemData.status === 'Checked Out' && itemData.reservationId === reservationId) {
      tx.update(itemRef, {
        status: 'Available',
        currentHolderId: null,
        currentHolderName: null,
        lastCheckinTime: new Date().toISOString(),
        reservationId: null
      });
    }
    
    // Mark reservation as returned
    tx.update(resRef, {
      status: 'returned',
      returnedAt: serverTimestamp(),
      returnedEarly: true, // Flag to indicate early return
      updatedAt: serverTimestamp()
    });
    
    return { itemId, reservationId, status: 'returned' };
  });
};

/**
 * Check out a reserved item (mark reservation as "checked_out")
 */
export const checkoutReservedItem = async (itemId, reservationId) => {
  const itemRef = doc(db, 'items', itemId);
  const resRef = doc(db, 'items', itemId, 'reservations', reservationId);
  
  return runTransaction(db, async (tx) => {
    const itemSnap = await tx.get(itemRef);
    const resSnap = await tx.get(resRef);
    
    if (!itemSnap.exists()) throw new Error('Item not found');
    if (!resSnap.exists()) throw new Error('Reservation not found');
    
    const itemData = itemSnap.data();
    const resData = resSnap.data();
    
    if (itemData.status !== 'Available') {
      throw new Error('Item is not available for checkout');
    }
    
    if (resData.status !== 'reserved') {
      throw new Error('Reservation is not in reserved status');
    }
    
    // Update item status
    tx.update(itemRef, {
      status: 'Checked Out',
      currentHolderId: resData.pid || resData.userId,
      currentHolderName: resData.userName || `PID: ${resData.pid}`,
      lastCheckoutTime: new Date().toISOString(),
      dueDate: resData.end.toDate().toISOString(),
      checkoutDurationDays: null,
      reservationId: reservationId
    });
    
    // Update reservation status
    tx.update(resRef, {
      status: 'checked_out',
      checkedOutAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { itemId, reservationId, status: 'checked_out' };
  });
};

/**
 * Check in a reserved item (mark reservation as "returned")
 */
export const checkinReservedItem = async (itemId, reservationId) => {
  const itemRef = doc(db, 'items', itemId);
  const resRef = doc(db, 'items', itemId, 'reservations', reservationId);
  
  return runTransaction(db, async (tx) => {
    const itemSnap = await tx.get(itemRef);
    const resSnap = await tx.get(resRef);
    
    if (!itemSnap.exists()) throw new Error('Item not found');
    if (!resSnap.exists()) throw new Error('Reservation not found');
    
    const itemData = itemSnap.data();
    const resData = resSnap.data();
    
    if (itemData.status !== 'Checked Out') {
      throw new Error('Item is not currently checked out');
    }
    
    if (resData.status !== 'checked_out') {
      throw new Error('Reservation is not checked out');
    }
    
    if (itemData.reservationId !== reservationId) {
      throw new Error('Item is not checked out for this reservation');
    }
    
    // Update item status back to available
    tx.update(itemRef, {
      status: 'Available',
      currentHolderId: null,
      currentHolderName: null,
      lastCheckinTime: new Date().toISOString(),
      reservationId: null
    });
    
    // Update reservation status
    tx.update(resRef, {
      status: 'returned',
      returnedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { itemId, reservationId, status: 'returned' };
  });
};

/**
 * Get active reservation for an item (if checked out via reservation)
 */
export const getActiveReservation = async (itemId) => {
  const reservationsRef = collection(db, 'items', itemId, 'reservations');
  const q = query(
    reservationsRef,
    where('status', '==', 'checked_out')
  );
  
  const snap = await getDocs(q);
  if (!snap.empty) {
    // Sort by checkedOutAt on client side if multiple found
    const reservations = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    reservations.sort((a, b) => {
      const aTime = a.checkedOutAt?.toMillis() || 0;
      const bTime = b.checkedOutAt?.toMillis() || 0;
      return bTime - aTime; // Descending order (most recent first)
    });
    return reservations[0];
  }
  return null;
};

/**
 * Get upcoming reservation for an item (next reservation starting soon)
 */
export const getUpcomingReservation = async (itemId) => {
  const now = new Date();
  const reservationsRef = collection(db, 'items', itemId, 'reservations');
  // Use single inequality filter to avoid composite index
  const q = query(
    reservationsRef,
    where('start', '>', toTs(now)),
    orderBy('start', 'asc')
  );
  
  const snap = await getDocs(q);
  // Filter for 'reserved' status on client side
  for (const doc of snap.docs) {
    const data = doc.data();
    if (data.status === 'reserved') {
      return { id: doc.id, ...data };
    }
  }
  return null;
};

/**
 * Get current user's reservations for an item
 */
export const getUserReservations = async (itemId, { userId = null, pid = null } = {}) => {
  const reservationsRef = collection(db, 'items', itemId, 'reservations');
  let q;
  
  if (userId) {
    q = query(
      reservationsRef,
      where('userId', '==', userId),
      orderBy('start', 'asc')
    );
  } else if (pid) {
    q = query(
      reservationsRef,
      where('pid', '==', pid),
      orderBy('start', 'asc')
    );
  } else {
    return [];
  }
  
  const snap = await getDocs(q);
  const reservations = [];
  snap.forEach((doc) => {
    const data = doc.data();
    // Filter for active reservations (reserved or checked_out) on client side
    if (data.status === 'reserved' || data.status === 'checked_out') {
      reservations.push({ id: doc.id, ...data });
    }
  });
  
  return reservations;
};

/**
 * Helper to map reservation to calendar event
 */
export const mapToCalendarEvent = (reservation, itemName) => {
  return {
    id: reservation.id,
    title: itemName || 'Reserved',
    start: reservation.start.toDate(),
    end: reservation.end.toDate(),
    status: reservation.status,
    pid: reservation.pid || null,
    userName: reservation.userName || null
  };
};


